import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Clock, MapPin, User, Mail, Phone, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";

// Simplified interfaces to prevent typecheck timeout
interface TokenData {
  booking_id: number;
  recipient_type: string;
  recipient_email: string;
  is_valid: boolean;
}

interface SimpleBooking {
  id: number;
  booking_date: string;
  start_time_local: string;
  end_time_local: string;
  status: string;
  note: string;
  decline_reason?: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  service_name: string;
  service_description: string;
  service_price: number;
  service_location: string;
}

export const PublicBookingView = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [bookingData, setBookingData] = useState<SimpleBooking | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid booking link");
      setLoading(false);
      return;
    }

    validateTokenAndFetchBooking();
  }, [token]);

  const validateTokenAndFetchBooking = async () => {
    try {
      // Validate the token
      const { data: validationData, error: validationError } = await supabase
        .rpc('validate_booking_token', { token_hash_input: token });

      if (validationError) {
        console.error('Token validation error:', validationError);
        setError("Unable to validate booking link");
        return;
      }

      if (!validationData || validationData.length === 0) {
        setError("Booking link not found");
        return;
      }

      const validation = validationData[0] as TokenData;
      
      if (!validation.is_valid) {
        setError("This booking link has expired or is no longer valid");
        return;
      }

      setTokenData(validation);

      // Fetch booking details with simpler query
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_date,
          start_time_local,
          end_time_local,
          status,
          note,
          decline_reason,
          customer_name,
          customer_email,
          customer_phone,
          customer_address,
          services (name, description, price, location)
        `)
        .eq('id', validation.booking_id)
        .single();

      if (bookingError) {
        console.error('Booking fetch error:', bookingError);
        setError("Unable to load booking details");
        return;
      }

      // Simplified data structure
      const booking: SimpleBooking = {
        id: bookingData.id,
        booking_date: bookingData.booking_date,
        start_time_local: bookingData.start_time_local,
        end_time_local: bookingData.end_time_local,
        status: bookingData.status,
        note: bookingData.note,
        decline_reason: bookingData.decline_reason,
        customer_name: bookingData.customer_name || '',
        customer_email: bookingData.customer_email || '',
        customer_phone: bookingData.customer_phone || '',
        customer_address: bookingData.customer_address || '',
        service_name: (bookingData.services as any)?.name || '',
        service_description: (bookingData.services as any)?.description || '',
        service_price: (bookingData.services as any)?.price || 0,
        service_location: (bookingData.services as any)?.location || ''
      };

      setBookingData(booking);
    } catch (error) {
      console.error('Error loading booking:', error);
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      new: { label: "New", variant: "secondary" as const, icon: AlertCircle },
      confirmed: { label: "Confirmed", variant: "default" as const, icon: CheckCircle2 },
      completed: { label: "Completed", variant: "secondary" as const, icon: CheckCircle2 },
      declined: { label: "Declined", variant: "destructive" as const, icon: XCircle },
      cancelled: { label: "Cancelled", variant: "destructive" as const, icon: XCircle },
      "removed by admin": { label: "Removed by Admin", variant: "secondary" as const, icon: XCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Unable to Load Booking</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!bookingData || !tokenData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Booking Not Found</h1>
            <p className="text-muted-foreground">The requested booking could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Booking Details</h1>
          <p className="text-muted-foreground">
            Viewing as: {tokenData.recipient_type} ({tokenData.recipient_email})
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Service & Booking Info */}
          <div className="space-y-6">
            {/* Service Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Service Details
                  {getStatusBadge(bookingData.status)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{bookingData.service_name}</h3>
                  {bookingData.service_description && (
                    <p className="text-muted-foreground mt-1">{bookingData.service_description}</p>
                  )}
                </div>
                
                {bookingData.service_price > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Price:</span>
                    <span className="text-lg font-bold text-primary">
                      Ksh {bookingData.service_price.toLocaleString()}
                    </span>
                  </div>
                )}

                {bookingData.service_location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-sm font-medium">Service Location</p>
                      <p className="text-sm text-muted-foreground">{bookingData.service_location}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Booking Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Booking Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Date</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(bookingData.booking_date), 'EEEE, MMMM do, yyyy')}
                    </p>
                  </div>
                </div>

                {bookingData.start_time_local && bookingData.end_time_local && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Time</p>
                      <p className="text-sm text-muted-foreground">
                        {bookingData.start_time_local} - {bookingData.end_time_local}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {bookingData.note && (
              <Card>
                <CardHeader>
                  <CardTitle>Customer Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{bookingData.note}</p>
                </CardContent>
              </Card>
            )}

            {/* Decline Reason */}
            {bookingData.decline_reason && (
              <Card className="border-destructive/20">
                <CardHeader>
                  <CardTitle className="text-destructive flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Decline Reason
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{bookingData.decline_reason}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Customer Information */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {bookingData.customer_name && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Name</p>
                      <p className="text-sm text-muted-foreground">{bookingData.customer_name}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{bookingData.customer_email}</p>
                  </div>
                </div>

                {bookingData.customer_phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Phone</p>
                      <p className="text-sm text-muted-foreground">{bookingData.customer_phone}</p>
                    </div>
                  </div>
                )}

                {bookingData.customer_address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-sm font-medium">Address</p>
                      <p className="text-sm text-muted-foreground">{bookingData.customer_address}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Security Notice */}
        <Card className="mt-8 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Security Notice</p>
                <p className="text-sm text-amber-700 mt-1">
                  This is a secure booking link that expires after 7 days. Do not share this link with others.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};